import { api } from "@convex/_generated/api"; // Use alias
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Modal } from "@/components/_shared/Modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface RoomSettingsModalProps {
	isOpen: boolean;
	onClose: () => void;
	raumId: string;
	// nutzerHash: string; // Entfernt
	initialSettings: {
		passwort?: string | null;
		uploadModus: "anonym" | "eingeloggt" | "besitzer";
		nurBesitzerLoeschen: boolean;
	};
	onSuccess: (message: string) => void;
	onError: (message: string) => void;
}

export function RoomSettingsModal({
	isOpen,
	onClose,
	raumId,
	// nutzerHash, // Entfernt
	initialSettings,
	onSuccess,
	onError,
}: RoomSettingsModalProps) {
	const [passwort, setPasswort] = useState(initialSettings.passwort || "");
	const [uploadModus, setUploadModus] = useState<
		"anonym" | "eingeloggt" | "besitzer"
	>(initialSettings.uploadModus);
	const [nurBesitzerLoeschen, setNurBesitzerLoeschen] = useState(
		initialSettings.nurBesitzerLoeschen,
	);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const updateSettingsMutation = useMutation(
		api.fileShare.raumEinstellungenAktualisieren,
	);

	// Reset state when modal opens with new initial settings
	useEffect(() => {
		if (isOpen) {
			setPasswort(initialSettings.passwort || "");
			setUploadModus(initialSettings.uploadModus);
			setNurBesitzerLoeschen(initialSettings.nurBesitzerLoeschen);
		}
	}, [isOpen, initialSettings]);

	const handleSave = async () => {
		setIsSubmitting(true);
		try {
			await updateSettingsMutation({
				raumId,
				passwort: passwort.trim() || undefined,
				uploadModus,
				nurBesitzerLoeschen,
			});
			onSuccess("Einstellungen erfolgreich gespeichert.");
			onClose(); // Close modal on success
		} catch (error: unknown) {
			const message = (error as Error).message || String(error);
			onError(`Fehler beim Speichern: ${message}`);
			console.error("Error updating room settings:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Raumeinstellungen">
			<div className="space-y-6 p-2">
				{/* Password Setting */}
				<div className="space-y-2">
					<Label htmlFor="room-password">Passwort (optional)</Label>
					<Input
						className="border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-700"
						id="room-password"
						onChange={(e) => setPasswort(e.target.value)}
						placeholder="Leer lassen für keinen Schutz"
						type="password"
						value={passwort}
					/>
					<p className="text-gray-500 text-xs dark:text-gray-400">
						Entferne das Passwort, um den Raum öffentlich zugänglich zu machen.
					</p>
				</div>

				{/* Upload Permission */}
				<div className="space-y-2 rounded-lg border p-4 dark:border-gray-700">
					<div className="space-y-0.5">
						<Label>Upload-Berechtigung</Label>
						<p className="text-gray-500 text-xs dark:text-gray-400">
							Wer darf Dateien in diesen Raum hochladen?
						</p>
					</div>
					<Select
						value={uploadModus}
						onValueChange={(v) =>
							setUploadModus(v as "anonym" | "eingeloggt" | "besitzer")
						}
					>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Upload-Modus wählen" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="anonym">Anonym</SelectItem>
							<SelectItem value="eingeloggt">Eingeloggt</SelectItem>
							<SelectItem value="besitzer">Besitzer</SelectItem>
						</SelectContent>
					</Select>
				</div>

				{/* Delete Permission */}
				<div className="flex items-center justify-between space-x-2 rounded-lg border p-4 dark:border-gray-700">
					<div className="space-y-0.5">
						<Label htmlFor="delete-permission">Lösch-Berechtigung</Label>
						<p className="text-gray-500 text-xs dark:text-gray-400">
							Wer darf Dateien aus diesem Raum löschen?
						</p>
					</div>
					<div className="flex items-center gap-2">
						<span className="text-gray-600 text-sm dark:text-gray-300">
							Uploader/Bes.
						</span>
						<Switch
							aria-label="Lösch-Berechtigung umschalten"
							checked={nurBesitzerLoeschen}
							id="delete-permission"
							onCheckedChange={setNurBesitzerLoeschen}
						/>
						<span className="text-gray-600 text-sm dark:text-gray-300">
							Nur Besitzer
						</span>
					</div>
				</div>

				{/* Action Buttons */}
				<div className="flex justify-end space-x-2 pt-2">
					<Button
						className="dark:border-gray-600"
						disabled={isSubmitting}
						onClick={onClose}
						type="button"
						variant="outline"
					>
						Abbrechen
					</Button>
					<Button disabled={isSubmitting} onClick={handleSave} type="button">
						{isSubmitting ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Speichern...
							</>
						) : (
							"Einstellungen speichern"
						)}
					</Button>
				</div>
			</div>
		</Modal>
	);
}
